import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Client } from "../schemas/clientSchema";
import {
  clientService,
  ClientFilters,
  ClientResult,
} from "../api/services/clientService";

export const useClients = (
  filters: ClientFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: clients,
    isLoading: isClientsLoading,
    refetch,
  } = useQuery<ClientResult, Error>({
    queryKey: ["clients", filters],
    queryFn: () => clientService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const useGetClientById = (id?: string) =>
    useQuery<Client, Error>({
      queryKey: ["client", id],
      queryFn: () => clientService.getById(id!),
      enabled: !!id,
    });

  const createClientMutation = useMutation({
    mutationFn: clientService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clientOptions"] });
      toast.success("Client created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create client");
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: clientService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clientOptions"] });
      toast.success("Client updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update client");
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: clientService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clientOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Client deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete client");
    },
  });

  return {
    clients,
    isLoading:
      isClientsLoading ||
      createClientMutation.isPending ||
      updateClientMutation.isPending ||
      deleteClientMutation.isPending,
    useGetClientById,
    refetchClients: refetch,
    createClient: createClientMutation.mutate,
    updateClient: updateClientMutation.mutate,
    deleteClients: deleteClientMutation.mutate,
  };
};
